<script setup lang="ts">
import { ref, computed } from 'vue'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import {
  User,
  LogOut,
  LogIn,
  Trash2,
  AlertTriangle,
  Link2,
  Loader2,
  HardDrive,
} from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useRouter } from 'vue-router'
import { isStandaloneMode, setAppMode } from '@/lib/dataProvider'
import DownloadApp from '@/components/DownloadApp.vue'
import type { Provider } from '@supabase/supabase-js'

const router = useRouter()
const authStore = useAuthStore()

const isStandalone = computed(() => isStandaloneMode())
const userEmail = computed(() => authStore.user?.email || 'Unknown')

// Delete Account
const showDeleteDialog = ref(false)
const deleteConfirmation = ref('')
const isDeleting = ref(false)
const deleteError = ref('')

const canDelete = computed(() => {
  return deleteConfirmation.value.toLowerCase() === userEmail.value.toLowerCase()
})

// Linked Accounts
const linkingProvider = ref<string | null>(null)
const isLinkingProvider = computed(() => linkingProvider.value !== null)

const linkedIdentities = computed(() => {
  return authStore.user?.identities || []
})

const isProviderLinked = (provider: string): boolean => {
  return linkedIdentities.value.some(identity => identity.provider === provider)
}

const handleSignOut = async () => {
  await authStore.signOut()
  router.push('/auth')
}

const handleLogin = () => {
  setAppMode('online')
  router.push('/auth')
  window.location.reload()
}

const openDeleteDialog = () => {
  deleteConfirmation.value = ''
  deleteError.value = ''
  showDeleteDialog.value = true
}

const handleDeleteAccount = async () => {
  if (!canDelete.value) return
  
  isDeleting.value = true
  deleteError.value = ''
  
  try {
    const result = await authStore.deleteAccount()
    if (!result.success) throw new Error(result.error)
    
    await authStore.signOut()
    showDeleteDialog.value = false
  } catch (error) {
    deleteError.value = error instanceof Error ? error.message : 'Failed to delete account'
  } finally {
    isDeleting.value = false
  }
}

const linkProvider = async (provider: Provider) => {
  linkingProvider.value = provider
  
  try {
    const result = await authStore.linkIdentity(provider as 'google' | 'discord' | 'spotify')
    if (!result.success) throw new Error(result.error)
  } catch (error) {
    console.error('Failed to link provider:', error)
    linkingProvider.value = null
  }
}

const unlinkProvider = async (provider: string) => {
  if (linkedIdentities.value.length <= 1) return
  
  linkingProvider.value = provider
  
  try {
    const result = await authStore.unlinkIdentity(provider)
    if (!result.success) throw new Error(result.error)
  } catch (error) {
    console.error('Failed to unlink provider:', error)
  } finally {
    linkingProvider.value = null
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- Section Header -->
    <div>
      <h3 class="text-sm font-medium">Account</h3>
      <p class="text-xs text-muted-foreground mt-1">Manage your account and connected identities</p>
    </div>

    <!-- Standalone Mode: Show login option -->
    <div v-if="isStandalone" class="space-y-4">
      <div class="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="h-12 w-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <HardDrive class="h-6 w-6 text-amber-500" />
            </div>
            <div>
              <div class="text-sm font-medium">Standalone Mode</div>
              <div class="text-xs text-muted-foreground">All data stored locally on this device</div>
            </div>
          </div>
        </div>
        <div class="mt-4 pt-4 border-t border-amber-500/20">
          <Button variant="default" class="w-full" @click="handleLogin">
            <LogIn class="h-4 w-4 mr-2" />
            Sign in to sync across devices
          </Button>
        </div>
      </div>
    </div>
    
    <!-- Online Mode: Show normal account info -->
    <div v-else class="space-y-6">
      <!-- User Profile Card -->
      <div class="rounded-xl border bg-card p-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <User class="h-6 w-6 text-primary" />
            </div>
            <div>
              <div class="text-sm font-medium">{{ userEmail }}</div>
              <div class="text-xs text-muted-foreground">Signed in</div>
            </div>
          </div>
          <Button variant="outline" size="sm" @click="handleSignOut">
            <LogOut class="h-4 w-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
      
      <!-- Linked Accounts Section -->
      <div class="space-y-3">
        <label class="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
          <Link2 class="h-3 w-3" />
          Linked Accounts
        </label>
        
        <div class="grid gap-2">
          <!-- Google -->
          <div 
            class="flex items-center justify-between p-3 rounded-lg border transition-all"
            :class="isProviderLinked('google') ? 'border-blue-500/30 bg-blue-500/5' : 'border-border'"
          >
            <div class="flex items-center gap-3">
              <div class="h-8 w-8 rounded-lg bg-white flex items-center justify-center">
                <svg class="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span class="text-sm font-medium">Google</span>
            </div>
            <Button
              v-if="isProviderLinked('google')"
              variant="ghost"
              size="sm"
              class="h-7 text-xs text-muted-foreground hover:text-foreground"
              @click="unlinkProvider('google')"
              :disabled="isLinkingProvider || linkedIdentities.length <= 1"
            >
              <Loader2 v-if="linkingProvider === 'google'" class="h-3 w-3 animate-spin" />
              <span v-else>Unlink</span>
            </Button>
            <Button
              v-else
              variant="outline"
              size="sm"
              class="h-7 text-xs"
              @click="linkProvider('google')"
              :disabled="isLinkingProvider"
            >
              <Loader2 v-if="linkingProvider === 'google'" class="h-3 w-3 animate-spin" />
              <span v-else>Link</span>
            </Button>
          </div>
          
          <!-- Discord -->
          <div 
            class="flex items-center justify-between p-3 rounded-lg border transition-all"
            :class="isProviderLinked('discord') ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-border'"
          >
            <div class="flex items-center gap-3">
              <div class="h-8 w-8 rounded-lg bg-[#5865F2] flex items-center justify-center">
                <svg class="w-5 h-5 text-white" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                </svg>
              </div>
              <span class="text-sm font-medium">Discord</span>
            </div>
            <Button
              v-if="isProviderLinked('discord')"
              variant="ghost"
              size="sm"
              class="h-7 text-xs text-muted-foreground hover:text-foreground"
              @click="unlinkProvider('discord')"
              :disabled="isLinkingProvider || linkedIdentities.length <= 1"
            >
              <Loader2 v-if="linkingProvider === 'discord'" class="h-3 w-3 animate-spin" />
              <span v-else>Unlink</span>
            </Button>
            <Button
              v-else
              variant="outline"
              size="sm"
              class="h-7 text-xs"
              @click="linkProvider('discord')"
              :disabled="isLinkingProvider"
            >
              <Loader2 v-if="linkingProvider === 'discord'" class="h-3 w-3 animate-spin" />
              <span v-else>Link</span>
            </Button>
          </div>
        </div>
      </div>
      
      <!-- Desktop App Download -->
      <DownloadApp :show-title="true" layout="horizontal" :show-description="false" />
      
      <!-- Danger Zone -->
      <div class="space-y-3 pt-2">
        <label class="text-xs font-medium text-destructive/70 uppercase tracking-wide">Danger Zone</label>
        <Button 
          variant="outline" 
          size="sm" 
          class="w-full justify-start text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
          @click="openDeleteDialog"
        >
          <Trash2 class="h-4 w-4 mr-2" />
          Delete Account
        </Button>
      </div>
    </div>

    <!-- Delete Account Confirmation Dialog -->
    <Dialog v-model:open="showDeleteDialog">
      <DialogContent>
        <DialogHeader>
          <DialogTitle class="flex items-center gap-2 text-destructive">
            <AlertTriangle class="h-5 w-5" />
            Delete Account Permanently
          </DialogTitle>
          <DialogDescription as="div" class="space-y-3">
            <p>
              This action <strong>cannot be undone</strong>. This will permanently delete your account and all associated data including:
            </p>
            <ul class="list-disc list-inside text-sm space-y-1 text-muted-foreground">
              <li>All your scenes and configurations</li>
              <li>Uploaded images and audio files</li>
              <li>Spotify and Hue bridge connections</li>
              <li>All other personal data</li>
            </ul>
            <div class="pt-2">
              <p class="text-sm mb-2">To confirm, type your email address: <strong>{{ userEmail }}</strong></p>
              <Input 
                v-model="deleteConfirmation" 
                placeholder="Enter your email to confirm"
                class="font-mono"
              />
            </div>
            <p v-if="deleteError" class="text-sm text-destructive">{{ deleteError }}</p>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="showDeleteDialog = false" :disabled="isDeleting">
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            @click="handleDeleteAccount"
            :disabled="!canDelete || isDeleting"
          >
            {{ isDeleting ? 'Deleting...' : 'Delete My Account' }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
